import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Package, AlertCircle, TrendingDown, ArrowRight, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const GET_INVENTORY_OVERVIEW = gql`
  query GetInventoryOverview($catalogId: ID!) {
    inventoryOverview(catalogId: $catalogId) {
      totalManagedItems
      lowStockCount
      outOfStockCount
      totalStockValue
      topLowStockItems {
        id
        name
        stockLevel
        threshold
        variant
      }
    }
  }
`;

const InventoryOverview = ({ catalog }) => {
    const navigate = useNavigate();

    const { data: inventoryData, loading } = useQuery(GET_INVENTORY_OVERVIEW, {
        variables: { catalogId: catalog?.id },
        skip: !catalog?.id,
        fetchPolicy: 'cache-and-network'
    });

    const inventoryStats = useMemo(() => {
        if (!inventoryData?.inventoryOverview) {
            return {
                totalManagedItems: 0,
                lowStockCount: 0,
                outOfStockCount: 0,
                totalStockValue: 0,
                topLowStockItems: []
            };
        }
        return inventoryData.inventoryOverview;
    }, [inventoryData]);

    const getStockStatus = (stockLevel, threshold) => {
        if (stockLevel <= 0) return {
            color: 'text-red-600',
            bg: 'bg-red-50 border-red-200',
            text: 'Out of Stock'
        };
        if (stockLevel < threshold) return {
            color: 'text-amber-600',
            bg: 'bg-amber-50 border-amber-200',
            text: `${stockLevel} left`
        };
        return {
            color: 'text-green-600',
            bg: 'bg-green-50 border-green-200',
            text: `${stockLevel} in stock`
        };
    };

    const hasIssues = inventoryStats.lowStockCount > 0 || inventoryStats.outOfStockCount > 0;

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="h-8 bg-gray-200 rounded"></div>
                        <div className="h-8 bg-gray-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Compact Header */}
            <div className="p-4 pb-3">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-gray-600" />
                        <h3 className="font-semibold text-gray-900 text-lg">Inventory</h3>
                        {hasIssues && (
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        )}
                    </div>
                    <button
                        onClick={() => navigate('/inventory')}
                        className="flex items-center bg-gray-50 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                        View All <ChevronRight className="h-3 w-3 ml-1" />
                    </button>
                </div>

                {/* Compact Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-lg font-bold text-gray-900">
                            {inventoryStats.totalManagedItems}
                        </div>
                        <div className="text-sm text-gray-500">Items</div>
                    </div>

                    {/* <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-lg font-bold text-green-600">
                            ₦{(inventoryStats.totalStockValue / 1000)?.toFixed(0) || '0'}k
                        </div>
                        <div className="text-sm text-gray-500">Value</div>
                    </div> */}

                    {hasIssues && (
                        <>
                            {inventoryStats.lowStockCount > 0 && (
                                <div className="text-center p-2 bg-amber-50 rounded border border-amber-200">
                                    <div className="text-lg font-bold text-amber-600">
                                        {inventoryStats.lowStockCount}
                                    </div>
                                    <div className="text-sm text-amber-700">Low Stock</div>
                                </div>
                            )}

                            {inventoryStats.outOfStockCount > 0 && (
                                <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                                    <div className="text-lg font-bold text-red-600">
                                        {inventoryStats.outOfStockCount}
                                    </div>
                                    <div className="text-sm text-red-700">Out of Stock</div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Alert Section - Only when there are issues */}
            {hasIssues && inventoryStats.topLowStockItems?.length > 0 && (
                <div className="px-4 pb-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <span className="text-sm font-medium text-amber-800">
                                {inventoryStats.topLowStockItems.length} item{inventoryStats.topLowStockItems.length > 1 ? 's' : ''} need attention
                            </span>
                        </div>

                        <div className="space-y-2">
                            {inventoryStats.topLowStockItems.slice(0, 2).map((item) => {
                                const status = getStockStatus(item.stockLevel, item.threshold);
                                return (
                                    <div key={item.id} className="flex justify-between items-center text-sm">
                                        <span className="text-gray-900 truncate">
                                            {item.name}
                                            {item.variant && item.variant !== 'default' && (
                                                <span className="text-sm text-gray-500 ml-1">
                                                    ({item.variant})
                                                </span>
                                            )}
                                        </span>
                                        <span className={`font-medium ${status.color}`}>
                                            {status.text}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {inventoryStats.topLowStockItems.length > 2 && (
                            <button
                                onClick={() => navigate('/inventory?filter=low')}
                                className="w-full text-center text-sm text-amber-700 hover:text-amber-800 font-medium mt-2 pt-2 border-t border-amber-200"
                            >
                                View {inventoryStats.topLowStockItems.length - 2} more items
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Success State - Compact */}
            {inventoryStats.totalManagedItems > 0 && !hasIssues && (
                <div className="px-4 pb-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="flex items-center justify-center gap-2 text-green-700">
                            <Package className="h-4 w-4" />
                            <span className="text-sm font-medium">All items well stocked</span>
                        </div>
                    </div>
                </div>
            )}

            {/* No Tracking State - Compact */}
            {inventoryStats.totalManagedItems === 0 && (
                <div className="px-4 pb-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        <TrendingDown className="h-5 w-5 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-2">No inventory tracking</p>
                        <button
                            onClick={() => navigate('/inventory')}
                            className="text-sm text-blue-600 bg-gray-50 hover:text-blue-700 font-medium"
                        >
                            Set up tracking
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryOverview;